import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import type { ReactNode, Ref } from "react";
import { cn } from "../../lib/cn";

const alertTones = {
  info: { box: "bg-info-soft border-info/30 text-info-fg", Icon: Info },
  success: { box: "bg-success-soft border-success/30 text-success-fg", Icon: CircleCheck },
  warning: { box: "bg-warning-soft border-warning/30 text-warning-fg", Icon: TriangleAlert },
  danger: { box: "bg-danger-soft border-danger/30 text-danger-fg", Icon: CircleAlert },
} as const;

/** Banner with a status tint: icon, bold title, one line, optional action. */
export function Alert({
  tone = "info",
  title,
  children,
  action,
  className,
  ref,
  tabIndex,
}: {
  tone?: keyof typeof alertTones;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
  ref?: Ref<HTMLDivElement>;
  /** -1 lets a form's error take focus when it appears (`useRevealError`). */
  tabIndex?: number;
}) {
  const { box, Icon } = alertTones[tone];
  return (
    <div
      ref={ref}
      tabIndex={tabIndex}
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
      className={cn(
        "flex gap-3 rounded-md border px-4 py-3 text-sm",
        tabIndex !== undefined && "scroll-mt-4 focus:outline-none",
        box,
        className,
      )}
    >
      <Icon aria-hidden className="mt-0.5 size-4 shrink-0" strokeWidth={2} />
      <div className="min-w-0 flex-1">
        <p className="font-bold">{title}</p>
        {children && <div className="mt-0.5 text-ink-2">{children}</div>}
      </div>
      {action}
    </div>
  );
}

const badgeTones = {
  neutral: "bg-surface-2 text-ink-2",
  brand: "bg-brand-soft text-brand-fg",
  success: "bg-success-soft text-success-fg",
  warning: "bg-warning-soft text-warning-fg",
  danger: "bg-danger-soft text-danger-fg",
} as const;

/** Status is always a word plus colour plus a dot (design system rule). */
export function Badge({
  tone = "neutral",
  children,
}: {
  tone?: keyof typeof badgeTones;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 font-semibold text-xs",
        badgeTones[tone],
      )}
    >
      <span aria-hidden className="size-1.5 rounded-full bg-current" />
      {children}
    </span>
  );
}

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section className={cn("rounded-lg border border-edge bg-surface shadow-xs", className)}>
      {children}
    </section>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <span aria-hidden className={cn("block animate-pulse rounded-sm bg-surface-3", className)} />
  );
}

export function EmptyState({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <span className="grid size-12 place-items-center rounded-[14px] bg-brand-soft text-brand-fg">
        {icon}
      </span>
      <h3 className="font-bold text-ink text-lg">{title}</h3>
      {children && <p className="max-w-sm text-ink-2">{children}</p>}
      {action}
    </div>
  );
}
